import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByIdAndZone } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import Block from "@store/Components/Common/Block";
import {
    descriptionWrapperMaxHeight,
    storeProductDetailsViewStyles as styles,
} from "@store/Components/Store/StoreProductDetailsView";
import { useIsSignedIn } from "@store/Hooks/Common";
import {
    useCanAddToCart,
    useHasAttributes,
    useHasDescription,
    useHasDocuments,
    useHasImages,
} from "@store/Hooks/Product";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import scrollToId from "@store/utilities/scrollToId";
import stripHtml from "@store/utilities/stripHtml";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { createRef, FC, useContext, useEffect, useState } from "react";
import { connect } from "react-redux";

import { withTheme } from "styled-components";

const StyledSpan = getStyledWrapper("span");
const PageTitle = getWidgetComponent("ProductDetails/ProductDetailsPageTitle");
const PartNumber = getWidgetComponent("ProductDetails/ProductDetailsPartNumber");
const PrimaryImage = getWidgetComponent("ProductDetails/ProductDetailsPrimaryImage");
const ImageCarousel = getWidgetComponent("ProductDetails/ProductDetailsImageCarousel");
const Description = getWidgetComponent("ProductDetails/ProductDetailsDescription");
const Attributes = getWidgetComponent("ProductDetails/ProductDetailsAttributes");
const Documents = getWidgetComponent("ProductDetails/ProductDetailsDocuments");
const ProductVariants = getWidgetComponent("ProductDetails/ProductDetailsVariantOptions");
const AddToCart = getWidgetComponent("ProductDetails/ProductDetailsAddToCartButton");
const AddToList = getWidgetComponent("ProductDetails/ProductDetailsAddToListLink");
const QuantityOrdered = getWidgetComponent("ProductDetails/ProductDetailsQuantityOrdered");
const ProductDetailsPrice = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const descriptionWrapper = createRef<HTMLSpanElement>();
const ImagesDownloadButton = getWidgetComponent("ProductDetails/ProductDetailsImagesDownloadButton");

type OwnProps = WidgetProps & PageProps;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const currentPage = getCurrentPage(state);
    const pageId = currentPage.id;
    const widgets = pageId ? getWidgetsByIdAndZone(state, ownProps.id, "relatedProducts", pageId) : undefined;
    const hasCarouselProducts = widgets?.some(widget => {
        if (widget.type === "Common/ProductCarousel") {
            const prods = getProductsForProductInfoList(state, widget.id);
            return prods && prods.length > 0;
        }
    });

    return {
        hasCarouselProducts,
    };
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const StoreProductDetailsView: FC<Props> = props => {
    const { product, productInfo } = useContext(ProductContext);
    const canAddToCart = useCanAddToCart();
    const isSignedIn = useIsSignedIn();
    const hasDescription = useHasDescription();
    const hasAttributes = useHasAttributes();
    const hasDocuments = useHasDocuments();
    const hasImages = useHasImages();
    const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(false);
    const [specificationsExpanded, setSpecificationsExpanded] = useState<boolean>(true);
    const [resourcesExpanded, setResourcesExpanded] = useState<boolean>(true);
    const shortDimensions = product.properties.shortDimensions;
    const hasShortDimensions = shortDimensions !== "";
    const { id, hasCarouselProducts } = props;
    const minimumOrderQty = product.minimumOrderQty;
    const hasMinimumOrderQty = minimumOrderQty > 1;
    const qtyOnHand = productInfo.inventory?.qtyOnHand || 0;
    const qtyOnHandText =
        qtyOnHand <= 5
            ? `${translate("Only {0} left", `${qtyOnHand}`)}`
            : `${translate("{0} in stock ", `${qtyOnHand}`)}`;

    const toggleDescription = () => {
        setDescriptionExpanded(!descriptionExpanded);
    };

    const toggleSpecifications = () => {
        setSpecificationsExpanded(!specificationsExpanded);
    };

    const toggleResources = () => {
        setResourcesExpanded(!resourcesExpanded);
    };

    const navigateToDescription = () => {
        setDescriptionExpanded(true);
        scrollToId("description");
    };

    const navigateToSpecifications = () => {
        setSpecificationsExpanded(true);
        scrollToId("specifications");
    };

    const navigateToResources = () => {
        setResourcesExpanded(true);
        scrollToId("resources");
    };

    const navigateToCarousel = () => {
        scrollToId("carousel");
    };

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };
    const printLabel = translate("Print");

    return (
        <>
            <Block {...styles.detail?.block}>
                <StyledWrapper {...styles.detail?.printLinkWrapper}>
                    <Link {...styles.detail?.printLink} onClick={printOrOpenPrintAllModal}>
                        {printLabel}
                    </Link>
                </StyledWrapper>
                <GridContainer {...styles.detail?.container}>
                    <GridItem {...styles.detail?.verticalCarouselGridItem}>
                        <ImageCarousel isVertical />
                    </GridItem>
                    <GridItem {...styles.detail?.verticalCarouselImageGridItem}>
                        <PrimaryImage />
                    </GridItem>
                    <GridItem {...styles.detail?.carouselGridItem}>
                        <PrimaryImage />
                        <ImageCarousel />
                    </GridItem>
                    <GridItem {...styles.detail?.contentGridItem}>
                        <GridContainer {...styles.detail?.content?.container}>
                            {product.brand ? (
                                <GridItem {...styles.detail?.content?.gridItem}>
                                    <ProductBrand brand={product.brand} />
                                </GridItem>
                            ) : null}
                            <GridItem {...styles.detail?.content?.gridItem}>
                                <PageTitle />
                            </GridItem>
                            {product.productNumber ? (
                                <GridItem {...styles.detail?.content?.gridItem}>
                                    <PartNumber />
                                </GridItem>
                            ) : null}
                            {product.trackInventory ? (
                                <GridItem {...styles.detail?.content?.availabilityGridItem}>
                                    <ProductContextAvailability />
                                    {qtyOnHand > 0 ? (
                                        <Typography {...styles.detail?.content?.qtyOnHandText}>
                                            {qtyOnHandText}
                                        </Typography>
                                    ) : null}
                                </GridItem>
                            ) : null}
                            {isSignedIn ? (
                                <GridItem {...styles.detail?.content?.priceGridItem}>
                                    {product.isVariantParent ? (
                                        <Typography {...styles.detail?.content?.productStartingAtText}>
                                            {translate("Starting at")}
                                        </Typography>
                                    ) : null}
                                    <ProductDetailsPrice />
                                </GridItem>
                            ) : null}
                            <GridItem {...styles.detail?.content?.verticalWrapperGridItem}>
                                <GridContainer {...styles.detail?.content?.mobileVerticalContainer}>
                                    <GridItem {...styles.detail?.content?.mobileVerticalCarouselGridItem}>
                                        <ImageCarousel isVertical />
                                    </GridItem>
                                    <GridItem {...styles.detail?.content?.mobileVerticalCarouselImageGridItem}>
                                        <PrimaryImage />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.detail?.content?.mobileCarouselGridItem}>
                                <PrimaryImage />
                                <ImageCarousel />
                            </GridItem>
                            {hasDescription ? (
                                <GridItem {...styles.detail?.content?.gridItem}>
                                    <Typography>
                                        <StyledSpan {...styles.detail?.content?.productShortDescriptionWrapper}>
                                            <Typography {...styles.detail?.content?.productShortDescription}>
                                                <StyledSpan ref={descriptionWrapper}>
                                                    {parse(
                                                        stripHtml(product?.content?.htmlContent || ""),
                                                        parserOptions,
                                                    )}
                                                </StyledSpan>
                                            </Typography>
                                        </StyledSpan>
                                        <ReadMoreLink onClick={navigateToDescription} />
                                    </Typography>
                                </GridItem>
                            ) : null}
                            {hasAttributes ? (
                                <GridItem {...styles.detail?.content?.productSpecificationsgridItem}>
                                    {hasShortDimensions ? (
                                        <Typography {...styles.detail?.content?.productShortSpecificationsText}>
                                            <StyledSpan {...styles.detail?.content?.productShortSpecificationsLabel}>
                                                {translate("Dimensions:")}
                                            </StyledSpan>
                                            {shortDimensions}
                                        </Typography>
                                    ) : null}
                                    {isSignedIn && hasMinimumOrderQty ? (
                                        <Typography {...styles.detail?.content?.productShortSpecificationsText}>
                                            <StyledSpan {...styles.detail?.content?.productShortSpecificationsLabel}>
                                                {translate("Minimum quantity:")}
                                            </StyledSpan>
                                            {minimumOrderQty}
                                        </Typography>
                                    ) : null}
                                    <Link
                                        {...styles.detail?.content?.productSeeAllSpecificationsLink}
                                        onClick={navigateToSpecifications}
                                    >
                                        {translate("See all Specifications")}
                                    </Link>
                                </GridItem>
                            ) : null}
                            {product.variantTraits ? (
                                <GridItem {...styles.detail?.content?.productVariantGridItem}>
                                    <ProductVariants />
                                </GridItem>
                            ) : null}
                            {(product.canAddToWishlist || canAddToCart) && isSignedIn ? (
                                <GridItem {...styles.detail?.content?.gridItem}>
                                    <StyledWrapper {...styles.detail?.content?.actionsWrapper}>
                                        {canAddToCart && (
                                            <>
                                                <QuantityOrdered />
                                                <AddToCart />
                                            </>
                                        )}
                                        <StyledWrapper {...styles.detail?.content?.actionsInnerWrapper}>
                                            <ProductAddToListLink
                                                extendedStyles={styles.detail?.content?.addToListLink}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            ) : null}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </Block>
            {hasDescription || hasAttributes || hasDocuments || hasImages || hasCarouselProducts ? (
                <Block>
                    <StyledWrapper {...styles.detail?.productNavigation?.nav}>
                        {hasAttributes && (
                            <Link {...styles.detail?.productNavigation?.link} onClick={navigateToSpecifications}>
                                {translate("Specifications")}
                            </Link>
                        )}
                        {hasDescription && (
                            <Link {...styles.detail?.productNavigation?.link} onClick={navigateToDescription}>
                                {translate("Description")}
                            </Link>
                        )}
                        {(hasDocuments || hasImages) && (
                            <Link {...styles.detail?.productNavigation?.link} onClick={navigateToResources}>
                                {translate("Resources")}
                            </Link>
                        )}
                        {hasCarouselProducts && (
                            <Link {...styles.detail?.productNavigation?.link} onClick={navigateToCarousel}>
                                {translate("Related Products")}
                            </Link>
                        )}
                    </StyledWrapper>
                </Block>
            ) : null}
            {hasDescription || hasAttributes || hasDocuments || hasImages || product.specifications?.length ? (
                <Block {...styles.accordionBlock}>
                    <Accordion headingLevel={2}>
                        {hasAttributes ? (
                            <AccordionSection
                                title={translate("Specifications")}
                                expanded={specificationsExpanded}
                                onTogglePanel={toggleSpecifications}
                                headerProps={{ id: "specifications" }}
                            >
                                <Attributes />
                            </AccordionSection>
                        ) : null}
                        {hasDescription ? (
                            <AccordionSection
                                title={translate("Description")}
                                expanded={descriptionExpanded}
                                onTogglePanel={toggleDescription}
                                headerProps={{ id: "description" }}
                            >
                                <Description />
                            </AccordionSection>
                        ) : null}
                        {hasImages || hasDocuments ? (
                            <AccordionSection
                                title={translate("Resources")}
                                expanded={resourcesExpanded}
                                onTogglePanel={toggleResources}
                                headerProps={{ id: "resources" }}
                            >
                                {hasImages ? <ImagesDownloadButton /> : null}
                                {hasDocuments ? <Documents /> : null}
                            </AccordionSection>
                        ) : null}
                        {product.specifications?.map(tab => (
                            <ManagedAccordionSection title={tab.nameDisplay}>
                                {parse(tab.htmlContent, parserOptions)}
                            </ManagedAccordionSection>
                        ))}
                    </Accordion>
                </Block>
            ) : null}
            <Block>
                <span id="carousel"></span>
                <Zone contentId={id} zoneName="relatedProducts" />
            </Block>
        </>
    );
};

const ReadMoreLink: FC<LinkProps> = withTheme(({ ...otherProps }) => {
    const { applyProp } = applyPropBuilder(otherProps, { component: "link" });
    const { product } = useContext(ProductContext);
    const [descriptionHeight, setDescriptionHeight] = useState<number>(descriptionWrapper.current?.offsetHeight || 0);
    const [initialVariantId, setInitialVariantId] = useState(product.id);
    const descriptionIsOverflowing = descriptionHeight > descriptionWrapperMaxHeight;
    const onVariantIdChange = product.id !== initialVariantId;

    const setDescriptionHeightHandler = () => {
        setDescriptionHeight(descriptionWrapper.current?.offsetHeight || 0);
    };

    const setInitialVariantIdHandler = () => {
        setInitialVariantId(product.id);
    };

    useEffect(() => {
        const debouncedHandleResize = debounce(function handleResize() {
            setDescriptionHeightHandler();
        }, 300);

        window.addEventListener("resize", debouncedHandleResize);

        return () => {
            window.removeEventListener("resize", debouncedHandleResize);
        };
    }, []);

    useEffect(() => {
        if (descriptionWrapper?.current) {
            setDescriptionHeightHandler();
        }
    }, [descriptionWrapper]);

    useEffect(() => {
        if (onVariantIdChange) {
            setDescriptionHeightHandler();
            setInitialVariantIdHandler();
        }
    }, [onVariantIdChange]);

    if (descriptionIsOverflowing) {
        return (
            <Link {...styles.detail?.content?.productReadMoreLink} onClick={applyProp("onClick")}>
                {translate("Read more")}
            </Link>
        );
    }
    return null;
});

export default connect(mapStateToProps)(StoreProductDetailsView);
